import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  Tooltip,
  IconButton,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert
} from '@mui/material';
import { MdInfoOutline, MdRefresh } from 'react-icons/md';

interface BreakdownItem {
  serviceName: string;
  cost: number;
}

export interface AzureCostData {
  totalCost: number;
  currency: string;
  breakdown: BreakdownItem[];
}

interface AzureCostModalProps {
  open: boolean;
  onClose: () => void;
  data: AzureCostData | undefined;
  isLoading: boolean;
  isError: boolean;
  onRefresh: () => void;
  totalTokens: number;
}

const explainService = (serviceName: string) => {
  const lower = serviceName.toLowerCase();
  if (lower.includes('cognitive') || lower.includes('openai')) return 'AI model requests and token usage';
  if (lower.includes('storage') || lower.includes('blob')) return 'File and asset storage';
  if (lower.includes('app service') || lower.includes('web')) return 'Application hosting';
  if (lower.includes('database') || lower.includes('sql')) return 'Database storage and queries';
  if (lower.includes('bandwidth') || lower.includes('network')) return 'Data transfer and networking';
  return 'General Azure service';
};

const AzureCostModal: React.FC<AzureCostModalProps> = ({
  open,
  onClose,
  data,
  isLoading,
  isError,
  onRefresh,
  totalTokens,
}) => {
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: 'var(--color-surface, #ffffff)',
          color: 'var(--color-text, #ffffff)',
          backgroundImage: 'none'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight="bold">Azure Cost Details</Typography>
        <IconButton onClick={onRefresh} disabled={isLoading} sx={{ color: 'var(--color-text)' }}>
          <MdRefresh />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers sx={{ borderColor: 'var(--color-border-grey, rgba(0,0,0,0.12))' }}>
        {isLoading && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <LinearProgress />
            <Typography sx={{ mt: 2 }} color="inherit">Fetching latest cost data...</Typography>
          </Box>
        )}

        {isError && !isLoading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load Azure Cost data. Please try refreshing.
          </Alert>
        )}

        {!isLoading && !isError && data && (
          <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Billing Period: <strong>Month-to-Date</strong>
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Last Updated: <strong>Recently</strong>
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>Total Actual Cost</Typography>
                <Typography variant="h4" sx={{ color: 'var(--color-stat-accent, #1976d2)', fontWeight: 'bold' }}>
                  {data.totalCost.toFixed(2)} {data.currency}
                </Typography>
              </Box>
            </Box>

            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              Service Breakdown
              <Tooltip title="Actual billed costs grouped by Azure Service. Note: Per-model cost breakdown is not currently provided by the billing API." placement="top" arrow>
                <IconButton size="small" sx={{ ml: 1, color: 'var(--color-text)', opacity: 0.9 }}><MdInfoOutline fontSize="small" /></IconButton>
              </Tooltip>
            </Typography>
            
            {data.breakdown.length === 0 ? (
              <Alert severity="info">No cost breakdown available for this period.</Alert>
            ) : (
              <TableContainer 
                component={Paper} 
                variant="outlined" 
                sx={{ 
                  mb: 4, 
                  backgroundColor: 'transparent',
                  color: 'var(--color-text)',
                  borderColor: 'var(--color-border-grey, rgba(0,0,0,0.12))'
                }}
              >
                <Table size="small">
                  <TableHead sx={{ backgroundColor: 'var(--color-divider, rgba(255,255,255,0.05))' }}>
                    <TableRow>
                      <TableCell sx={{ color: 'var(--color-text)', borderColor: 'var(--color-border-grey, rgba(0,0,0,0.12))' }}><strong>Service</strong></TableCell>
                      <TableCell sx={{ color: 'var(--color-text)', borderColor: 'var(--color-border-grey, rgba(0,0,0,0.12))' }}><strong>Explanation</strong></TableCell>
                      <TableCell align="right" sx={{ color: 'var(--color-text)', borderColor: 'var(--color-border-grey, rgba(0,0,0,0.12))' }}><strong>Cost</strong></TableCell>
                      <TableCell align="right" sx={{ color: 'var(--color-text)', borderColor: 'var(--color-border-grey, rgba(0,0,0,0.12))' }}><strong>% of Total</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.breakdown.map((item, index) => {
                      const percent = data.totalCost > 0 ? (item.cost / data.totalCost) * 100 : 0;
                      return (
                        <TableRow key={index}>
                          <TableCell sx={{ color: 'var(--color-text)', borderColor: 'var(--color-border-grey, rgba(0,0,0,0.12))' }}>{item.serviceName}</TableCell>
                          <TableCell sx={{ color: 'var(--color-text)', opacity: 0.9, borderColor: 'var(--color-border-grey, rgba(0,0,0,0.12))' }}>{explainService(item.serviceName)}</TableCell>
                          <TableCell align="right" sx={{ color: 'var(--color-text)', borderColor: 'var(--color-border-grey, rgba(0,0,0,0.12))' }}>{item.cost.toFixed(2)} {data.currency}</TableCell>
                          <TableCell align="right" sx={{ color: 'var(--color-text)', borderColor: 'var(--color-border-grey, rgba(0,0,0,0.12))' }}>{percent.toFixed(1)}%</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Divider sx={{ my: 3, borderColor: 'var(--color-border-grey, rgba(0,0,0,0.12))' }} />

            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              AI Model & Token Usage
              <Tooltip title="Aggregated token usage across the platform." placement="top" arrow>
                <IconButton size="small" sx={{ ml: 1, color: 'var(--color-text)', opacity: 0.9 }}><MdInfoOutline fontSize="small" /></IconButton>
              </Tooltip>
            </Typography>
            
            <Alert severity="info" sx={{ mb: 2, backgroundColor: 'rgba(25, 118, 210, 0.1)', color: 'var(--color-text)' }}>
              While granular AI model/deployment data (e.g. per-model costs) is missing from the billing API, we tracked a total of <strong>{totalTokens.toLocaleString()}</strong> tokens used this month across the platform.
            </Alert>
            
            <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Tooltip title="Sum of all Prompt and Completion tokens processed." arrow>
                <Typography variant="caption" sx={{ borderBottom: '1px dotted', cursor: 'help', display: 'flex', alignItems: 'center', opacity: 0.9 }}>
                  Total Platform Tokens: {totalTokens.toLocaleString()} <MdInfoOutline fontSize="inherit" style={{ marginLeft: 4 }} />
                </Typography>
              </Tooltip>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ borderColor: 'var(--color-border-grey, rgba(0,0,0,0.12))' }}>
        <Button onClick={onClose} sx={{ color: 'var(--color-stat-accent, #1976d2)' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AzureCostModal;
